import { QuestionFilters, getQuestionsPage } from "./questionService";
import { generateFlashcardsFromTopic } from "./iaFlashcardService";
import { collection, getFirestore, limit, query, where, getDocs } from "firebase/firestore";
import { sanitizeFront, sanitizeBack } from "../scripts/helpers/flashcardSynthesizer";
import { getProfessorPobreExplanation } from "./aiService";

export interface Flashcard {
  id: string;
  front: string;
  back: string;
  subject?: string;
  topic?: string;
  banca?: string;
  year?: number;
  sourceQuestionId?: string;
  source?: "question" | "ia" | "cache";
}

const FLASHCARDS_COLLECTION = "flashcards";

const MC_REGEX = /(assinale|alternativa|op[cç][aã]o correta|marque a alternativa|assinale a opcao)/i;
const isObjectiveQuestion = (q: { options?: any[]; correctOptionId?: any; text?: string }) => {
  const hasMultipleOptions = Array.isArray(q?.options) && q.options.length > 1;
  const hasGabarito = Boolean(q?.correctOptionId);
  return hasMultipleOptions || hasGabarito || MC_REGEX.test(q?.text || "");
};
const isLikelyObjectiveCard = (front?: string, back?: string) => {
  const text = `${front || ""} ${back || ""}`;
  return MC_REGEX.test(text);
};

const PLACEHOLDER_PATTERNS = [
  "conceito-chave",
  "definição resumida",
  "resumo curto",
  "ponto importante",
  "aplicação prática",
  "dica: leia a lei seca e resolva 5 questões",
  ": tópico",
  "de tópico",
  "qual a definição objetiva de tópico",
  "tópico: escreva em 1 linha",
];

const isLowQualityPlaceholderCard = (front?: string, back?: string) => {
  const text = `${front || ""} ${back || ""}`.toLowerCase();
  return PLACEHOLDER_PATTERNS.some((p) => text.includes(p));
};

export async function getFlashcardsFromQuestions(
  filters?: QuestionFilters & { bias?: "hard" | "easy" | "balanced" },
  maxCards: number = 20,
): Promise<Flashcard[]> {
  const cards: Flashcard[] = [];
  const db = getFirestore();

  // 1) Tenta cache de flashcards gerados (coleção flashcards)
  try {
    const constraints = [] as any[];
    if (filters?.subject) constraints.push(where("subject", "==", filters.subject));
    if (filters?.topic) constraints.push(where("topic", "==", filters.topic));
    constraints.push(limit(maxCards * 2)); // busca um pouco mais para poder filtrar
    const ref = query(collection(db, FLASHCARDS_COLLECTION), ...constraints);
    const snap = await getDocs(ref);
    snap.forEach((docSnap) => {
      const data = docSnap.data();
      if (isLikelyObjectiveCard(data.front, data.back)) return; // pular cartões de questão objetiva
      if (isLowQualityPlaceholderCard(data.front, data.back)) return; // pular placeholders antigos de baixa qualidade
      cards.push({
        id: docSnap.id,
        front: data.front,
        back: data.back,
        subject: data.subject,
        topic: data.topic,
        banca: data.banca,
        year: data.year,
        sourceQuestionId: data.sourceQuestionId,
        source: "cache",
      });
    });
  } catch (err) {
    console.error("Erro ao ler cache de flashcards", err);
  }

  // 2) Se cache insuficiente, gerar de questões (com fallback IA) e retornar, sem gravar
  if (cards.length < maxCards) {
    let cursor: any = null;
    const pageSize = 50;
    const maxPages = 6; // evita varrer a coleção inteira
    let page = 0;
    const seenIds = new Set(cards.map((c) => c.sourceQuestionId || c.id));

    while (cards.length < maxCards && page < maxPages) {
      const { questions, cursor: nextCursor, hasMore } = await getQuestionsPage(
        pageSize,
        cursor,
        filters,
      );

      // Reordena conforme bias de dificuldade
      const bias = filters?.bias || "balanced";
      const biased = [...questions].sort((a, b) => {
        if (bias === "hard") {
          return (b?.difficulty === "hard" ? 1 : 0) - (a?.difficulty === "hard" ? 1 : 0);
        }
        if (bias === "easy") {
          return (b?.difficulty === "easy" ? 1 : 0) - (a?.difficulty === "easy" ? 1 : 0);
        }
        return 0;
      });

      for (const q of biased) {
        if (cards.length >= maxCards) break;
        if (isObjectiveQuestion(q)) continue; // não usar questões de múltipla escolha
        if (seenIds.has(q.id)) continue; // dedup

        const front = sanitizeFront(q.text);
        let back = sanitizeBack(q as any);

        if ((!back || back.length < 15) && q.options && q.correctOptionId) {
          try {
            const question = {
              id: q.id,
              subject: q.subject || "Geral",
              topic: q.topic || "Geral",
              text: q.text,
              options: q.options,
              correctOptionId: q.correctOptionId,
              comments: q.comments,
            } as any;
            back = await getProfessorPobreExplanation(question, q.correctOptionId, "flashcard");
          } catch (err) {
            console.error("Falha ao gerar verso via Professor Pobre", err);
          }
        }

        if (!front || !back || back.length < 5) continue;
        if (isLowQualityPlaceholderCard(front, back)) continue;

        seenIds.add(q.id);
        cards.push({
          id: `q_${q.id}`,
          front,
          back,
          subject: q.subject,
          topic: q.topic,
          banca: q.banca,
          year: q.year,
          sourceQuestionId: q.id,
          source: "question",
        });
      }

      if (!hasMore) break;
      cursor = nextCursor;
      page += 1;
    }
  }

  // 3) Se ainda faltou, tenta IA (último recurso)
  if (cards.length === 0) {
    try {
      const iaCards = await generateFlashcardsFromTopic(filters, maxCards);
      cards.push(...iaCards);
    } catch (err) {
      console.error("Falha ao gerar flashcards via IA", err);
    }
  }

  return cards.slice(0, maxCards);
}
